import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, Video, Users, AlertCircle, Target } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import type { CreateMeetingForm, MeetingType, User, AppointmentSubjectType, AppointmentSubjectTypeInfo } from '../../types/meetings';

interface MeetingCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (meeting: CreateMeetingForm) => Promise<void>;
  isLoading?: boolean;
}

export const MeetingCreateModal: React.FC<MeetingCreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [subjectTypes, setSubjectTypes] = useState<AppointmentSubjectTypeInfo[]>([]);
  const [formData, setFormData] = useState<CreateMeetingForm>({
    title: '',
    description: '',
    meeting_type: 'IN_PERSON' as MeetingType,
    start_date: '',
    end_date: '',
    location: '',
    video_link: '',
    attendee_ids: [],
    subject_type: undefined
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load users and subject types for selection
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      loadSubjectTypes();
      // Reset form when modal opens
      setFormData({
        title: '',
        description: '',
        meeting_type: 'IN_PERSON',
        start_date: '',
        end_date: '',
        location: '',
        video_link: '',
        attendee_ids: [],
        subject_type: undefined
      });
      setErrors({});
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      console.log('Loading users, current user:', user);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .not('profile_type', 'is', null)
        .order('full_name');

      console.log('Users query result:', { data, error });
      if (error) throw error;

      // Filter out current user and users without profile_type
      const filteredUsers = (data || []).filter(u =>
        u.id !== user?.id &&
        u.profile_type &&
        u.profile_type !== null
      );

      console.log('Filtered users:', filteredUsers);
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      // Show empty state with error info
      setUsers([]);
    }
  };

  const loadSubjectTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('appointment_subject_types')
        .select('code, label, description, icon, color')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setSubjectTypes(data || []);
    } catch (error) {
      console.error('Error loading subject types:', error);
      setSubjectTypes([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'La date de début est requise';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'La date de fin est requise';
    }

    if (formData.start_date && formData.end_date && new Date(formData.start_date) >= new Date(formData.end_date)) {
      newErrors.end_date = 'La date de fin doit être après la date de début';
    }

    if (formData.meeting_type === 'IN_PERSON' && !formData.location.trim()) {
      newErrors.location = 'Le lieu est requis pour un rendez-vous en présentiel';
    }

    if (formData.meeting_type === 'VIRTUAL' && !formData.video_link.trim()) {
      newErrors.video_link = 'Le lien de visioconférence est requis';
    }

    if (formData.attendee_ids.length === 0) {
      newErrors.attendee_ids = 'Au moins un participant doit être sélectionné';
    }

    // Validate porteur rule
    if (user?.profile_type === 'PORTEUR') {
      const selectedUsers = users.filter(u => formData.attendee_ids.includes(u.id));
      const hasNonPorteur = selectedUsers.some(u => u.profile_type !== 'PORTEUR');
      if (!hasNonPorteur) {
        newErrors.attendee_ids = 'En tant que porteur, vous devez inviter au moins un accompagnateur, coordinateur, financeur ou administrateur';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error creating meeting:', error);
    }
  };

  const handleInputChange = (field: keyof CreateMeetingForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleAttendee = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      attendee_ids: prev.attendee_ids.includes(userId)
        ? prev.attendee_ids.filter(id => id !== userId)
        : [...prev.attendee_ids, userId]
    }));
  };

  const getProfileTypeLabel = (type: string) => {
    const labels = {
      PORTEUR: 'Porteur',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur',
      FINANCEUR: 'Financeur',
      ADMIN: 'Admin'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getProfileTypeColor = (type: string) => {
    const colors = {
      PORTEUR: 'bg-green-100 text-green-800',
      ACCOMPAGNATEUR: 'bg-blue-100 text-blue-800',
      COORDINATEUR: 'bg-purple-100 text-purple-800',
      FINANCEUR: 'bg-orange-100 text-orange-800',
      ADMIN: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Nouveau rendez-vous</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Titre du rendez-vous *
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Réunion de suivi projet"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Détails du rendez-vous..."
            />
          </div>

          {/* Subject Type */}
          <div>
            <label htmlFor="subject_type" className="block text-sm font-medium text-gray-700 mb-2">
              <Target className="w-4 h-4 inline mr-1" />
              Objet du rendez-vous (optionnel)
            </label>
            <select
              id="subject_type"
              value={formData.subject_type || ''}
              onChange={(e) => handleInputChange('subject_type', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Sélectionner un objet --</option>
              {subjectTypes.map((type) => (
                <option key={type.code} value={type.code}>
                  {type.label}
                </option>
              ))}
            </select>
            {formData.subject_type && subjectTypes.find(t => t.code === formData.subject_type) && (
              <p className="mt-2 text-sm text-gray-600">
                {subjectTypes.find(t => t.code === formData.subject_type)?.description}
              </p>
            )}
          </div>

          {/* Meeting Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de rendez-vous *
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="IN_PERSON"
                  checked={formData.meeting_type === 'IN_PERSON'}
                  onChange={(e) => handleInputChange('meeting_type', e.target.value as MeetingType)}
                  className="mr-2"
                />
                <MapPin className="w-4 h-4 mr-1" />
                Présentiel
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="VIRTUAL"
                  checked={formData.meeting_type === 'VIRTUAL'}
                  onChange={(e) => handleInputChange('meeting_type', e.target.value as MeetingType)}
                  className="mr-2"
                />
                <Video className="w-4 h-4 mr-1" />
                Visioconférence
              </label>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Début *
              </label>
              <input
                id="start_date"
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.start_date ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
              )}
            </div>
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Fin *
              </label>
              <input
                id="end_date"
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.end_date ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.end_date && (
                <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
              )}
            </div>
          </div>

          {/* Location or Video Link */}
          {formData.meeting_type === 'IN_PERSON' ? (
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Lieu *
              </label>
              <input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Adresse du rendez-vous"
              />
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location}</p>
              )}
            </div>
          ) : (
            <div>
              <label htmlFor="video_link" className="block text-sm font-medium text-gray-700 mb-2">
                <Video className="w-4 h-4 inline mr-1" />
                Lien de visioconférence *
              </label>
              <input
                id="video_link"
                type="url"
                value={formData.video_link}
                onChange={(e) => handleInputChange('video_link', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.video_link ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="https://meet.google.com/..."
              />
              {errors.video_link && (
                <p className="mt-1 text-sm text-red-600">{errors.video_link}</p>
              )}
            </div>
          )}

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Participants *
            </label>
            
            {user?.profile_type === 'PORTEUR' && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    En tant que porteur de projet, vous devez inviter au moins un accompagnateur, coordinateur, financeur ou administrateur.
                  </p>
                </div>
              </div>
            )}

            <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
              {users.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <p>Aucun utilisateur disponible</p>
                  <p className="text-xs mt-1">
                    Vérifiez que des accompagnateurs, coordinateurs, financeurs ou administrateurs sont enregistrés
                  </p>
                </div>
              ) : (
              users.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={formData.attendee_ids.includes(user.id)}
                    onChange={() => toggleAttendee(user.id)}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        {user.full_name || 'Utilisateur sans nom'}
                      </span>
                      {user.profile_type && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfileTypeColor(user.profile_type)}`}>
                          {getProfileTypeLabel(user.profile_type)}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))
              )}
            </div>
            
            {errors.attendee_ids && (
              <p className="mt-1 text-sm text-red-600">{errors.attendee_ids}</p>
            )}
            
            {formData.attendee_ids.length > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                {formData.attendee_ids.length} participant{formData.attendee_ids.length > 1 ? 's' : ''} sélectionné{formData.attendee_ids.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Création...</span>
                </div>
              ) : (
                'Créer le rendez-vous'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};